/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines */
import { WebsocketTypes } from '@/api/enumerations';
import {
  cancelDelegatedSession,
  getDelegatedSessionsByClient,
  getOversightClientsById,
  getOversightClientsCompany,
  getSessionsByClient,
  startDelegatedSession,
} from '@/api/oversight';
import {
  ClientProps,
  DelegatedAccessStatusUpdatePayload,
  DelegatedSessions,
  OversightClientCompanyProps,
  Sessions,
} from '@/api/oversight/types';
import { DelegatedAccessDialog } from '@/components/Dialog/DelegatedAccess';
import { Search } from '@/components/Search';
import { SkeletonWrapper } from '@/components/SkeletonWrapper';
import Snackbar from '@/components/Snackbar';
import { GridMain } from '@/components/UI/Grid';
import {
  IconAdminPanelSettingsMS,
  IconArrowCircleLeftMS,
  IconArrowForwardIosMS,
  IconCheckMS,
  IconPageviewMS,
} from '@/constants/icons';
import { GlobalContext } from '@/context/global';
import { WebsocketContext } from '@/context/websocketMessage';
import {
  formatCnpjCpf,
  formatDateAndTime,
  formatPhoneNumber,
  getCompanySubdomain,
} from '@/helpers';
import { getLocalStorageOversightToken } from '@/helpers/localStorage';
import { useDebounce } from '@/hooks/useDebounce';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { BackButton } from '@/pages/Hive/Tabs/CompanyInfo/styles';
import { Button } from '@/pages/Oversight/components/Button';
import { Card, CardData } from '@/pages/Oversight/components/Card';
import { CountBadge } from '@/pages/Oversight/components/Card/styles';
import { Table, TableHeadData } from '@/pages/Oversight/components/Table';
import {
  StyledTableCellBody,
  SubtitleCell,
} from '@/pages/Oversight/components/Table/styles';
import { TabItem } from '@/pages/Oversight/components/TabsPanel/types';
import { RoundedGreen } from '@/pages/Oversight/styles';
import {
  delegatedAccessLabels,
  formatDate,
  getTerminatedByLabel,
  normalizeStr,
} from '@/pages/Oversight/utils';
import {
  DragIndicator,
  Search as SearchIcon,
  WarningOutlined,
} from '@mui/icons-material';
import { Box, Dialog, Tooltip, Typography } from '@mui/material';
import jwtDecode from 'jwt-decode';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDefaultLayout } from 'react-resizable-panels';
import { useNavigate, useParams } from 'react-router-dom';

import { SectionClient } from '../SectionClient';
import { AmountOFClients, TableDataName } from '../styles';
import {
  CardContainer,
  CompanyMenuItem,
  CompanySelect,
  ContainerGroup,
  DelegatedSessionsContainer,
  Panel,
  Section,
  StyledSeparator,
  StyledTitle,
} from './styles';

const delegatedAccessRow = [
  { id: 1, label: 'Supervisor', key: 'supervisor_name' },
  { id: 2, label: 'Status', key: 'status' },
  { id: 3, label: 'Motivo', key: 'reason' },
  { id: 4, label: 'Ação', key: 'action', sortable: false },
] satisfies TableHeadData<DelegatedSessions>[];

const sectionClientRow = [
  { id: 1, label: 'Início da sessão', key: 'started_at' },
  { id: 2, label: 'Status', key: 'is_active' },
  { id: 3, label: 'Erros na sessão', key: 'client_error_count' },
  { id: 4, label: 'Ação', key: 'action', sortable: false },
] satisfies TableHeadData<Sessions>[];

const subscriptionTypeLabels: Record<string, string> = {
  monthly: 'Mensal',
  annual: 'Anual',
  yearly: 'Anual',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
};

function subscriptionLabel(type: string | null, installments: boolean): string {
  if (!type) return '-';
  const label = subscriptionTypeLabels[type] ?? type;
  return installments ? `${label} (parcelada)` : label;
}

function buildCompanyCardData(
  company: OversightClientCompanyProps,
  totalCompanies: number,
  companySelector: ReactNode
): CardData[] {
  return [
    {
      label: 'Empresa',
      labelBadge: totalCompanies > 1 && (
        <CountBadge>{totalCompanies} empresas</CountBadge>
      ),
      value: company.name ?? '-',
      valueElement: companySelector,
    },
    { label: 'CNPJ', value: company.cnpj ? formatCnpjCpf(company.cnpj) : '-' },
    {
      label: 'Laudos e amostras disponíveis',
      value: `${company.available_reports ?? 0} laudos • ${
        company.available_samples ?? 0
      } amostras`,
    },
    { label: 'Plano contratado', value: company.subscription_plan ?? '-' },
    {
      label: 'Assinatura',
      value: subscriptionLabel(company.subscription_type, company.installments),
    },
    {
      label: 'Próximo pagamento',
      value: company.next_payment
        ? formatDateAndTime(company.next_payment)
        : '-',
    },
  ];
}

export function expiredDetail(
  row: DelegatedSessions
): { label: string; tooltip: string } | null {
  if (row.status !== 'expired') {
    return null;
  }

  if (row.end_reason === 'approval_expired') {
    return {
      label: 'Prazo de início atingido',
      tooltip: 'Supervisor não iniciou o acesso delegado no prazo de 72 horas.',
    };
  }

  if (!row.consent_responded_at) {
    return {
      label: 'Prazo de resposta atingido',
      tooltip: 'Cliente não respondeu a solicitação no prazo de 24 horas.',
    };
  }

  return null;
}

export function CustomerDetails(): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [openDialogDelegatedAccess, setOpenDialogDelegatedAccess] =
    useState(false);
  const [tabs, setTabs] = useState<TabItem[]>([]);
  const [, setSelectedTab] = useState<string>('');
  const [infoClient, setInfoClient] = useState<CardData[]>([]);
  const [client, setClient] = useState<ClientProps>();
  const [delegatedSessions, setDelegatedSessions] = useState<
    DelegatedSessions[]
  >([]);
  const [searchDelegatedSessions, setSearchDelegatedSessions] = useState('');
  const [searchSessions, setSearchSessions] = useState('');
  const [sessions, setSessions] = useState<Sessions[]>([]);
  const [sessionsRecords, setSessionsRecords] = useState(0);
  const [delegatedRecords, setDelegatedRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [cancelTargetId, setCancelTargetId] = useState<number | null>(null);
  const [activitySessionId, setActivitySessionId] = useState<number>();
  const [companies, setCompanies] = useState<OversightClientCompanyProps[]>([]);
  const [companiesRecords, setCompaniesRecords] = useState(0);
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState(0);
  const [sectionErrorCount, setSectionErrorCount] = useState(0);
  const [device, setDevice] = useState('');
  const [endedAt, setEndedAt] = useState<string | null>('');
  const [startedAt, setStartAt] = useState<string | null>('');
  const [delegatedSessionId, setDelegatedSessionId] = useState<number | null>(
    null
  );

  const { id } = useParams();
  const { handleSnackbar } = useSnackbar();
  const { openSnackbar } = useContext(GlobalContext);
  const { websocketMessage } = useContext(WebsocketContext);
  const { getErrorMessage } = useErrorMessage();
  const debounceSessions = useDebounce(searchSessions);
  const debounceDelegatedSessions = useDebounce(searchDelegatedSessions);

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: 'unique-layout-id',
    panelIds: showDrawer ? ['left', 'right'] : ['left'],
  });
  const { oversightUserRole } = useTokenRefresh();

  const navigate = useNavigate();

  const oversightToken = getLocalStorageOversightToken();
  const loggedSupervisorId = oversightToken
    ? Number(jwtDecode<{ sub: string }>(oversightToken).sub)
    : null;

  const hasBlockingDelegatedAccess = delegatedSessions.some((session) =>
    ['pending_consent', 'approved', 'active'].includes(session.status)
  );

  const blockingAccessTooltip =
    'Já existe uma solicitação de acesso delegado aguardando aprovação do cliente.';

  const handleOpenDialog = (): void => {
    setOpenDialogDelegatedAccess(!openDialogDelegatedAccess);
  };

  const handleOpenActivity = (row: Sessions): void => {
    setShowDrawer(true);
    setActivitySessionId(row.id);
    setSectionErrorCount(row.client_error_count);
    setDevice(row.device);
    setEndedAt(row.ended_at);
    setStartAt(row.started_at);
    setDelegatedSessionId(row.delegated_session_id);

    const alreadyOpen = tabs.find((tab) => tab.id === row.id);
    if (alreadyOpen) {
      setSelectedTab(alreadyOpen.value);
      return;
    }

    const newTab: TabItem = {
      id: row.id,
      value: crypto.randomUUID(),
      title: 'Clientes',
      subtitle: row.started_at,
      source: 'section_client',
    };
    setTabs((prev) => [...prev, newTab]);
  };

  const handleClose = (): void => {
    setShowDrawer(false);
  };

  const getClientsById = useCallback(async () => {
    try {
      const { data } = await getOversightClientsById(Number(id));

      if (data) {
        setClient(data);
        setInfoClient([
          { label: 'Nome', value: data.name },
          {
            label: 'Status',
            value: data.is_active,
            user_status: data.is_active ? 'active' : 'inactive',
          },
          { label: 'ID', value: data.id },
          { label: 'CPF', value: formatCnpjCpf(data.cpf) },
          { label: 'E-mail', value: data.email },
          { label: 'Telefone', value: formatPhoneNumber(data.phone) },
        ]);
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  }, [handleSnackbar, getErrorMessage, id]);

  const getClientsCompany = useCallback(async () => {
    try {
      const { data } = await getOversightClientsCompany(Number(id));

      if (!data) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setCompanies(data.companies);
      setCompaniesRecords(data.records);
      setSelectedCompanyIndex(0);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  }, [handleSnackbar, getErrorMessage, id]);

  const infoCompany = useMemo<CardData[]>(() => {
    const selectedCompany = companies[selectedCompanyIndex];

    if (!selectedCompany) return [];

    const companySelector = companiesRecords > 1 && (
      <CompanySelect
        select
        size="small"
        value={selectedCompanyIndex}
        onChange={(e) => setSelectedCompanyIndex(Number(e.target.value))}
        SelectProps={{
          renderValue: () => selectedCompany.name ?? '-',
        }}
      >
        {companies.map((company, index) => (
          <CompanyMenuItem key={company.cnpj ?? index} value={index}>
            {company.name ?? `Empresa ${index + 1}`}
            {index === selectedCompanyIndex && IconCheckMS}
          </CompanyMenuItem>
        ))}
      </CompanySelect>
    );

    return buildCompanyCardData(
      selectedCompany,
      companiesRecords,
      companySelector
    );
  }, [companies, companiesRecords, selectedCompanyIndex]);

  const getDelegatedSessions = useCallback(
    async (params: string) => {
      try {
        const { data } = await getDelegatedSessionsByClient(
          Number(id),
          params,
          page
        );

        if (data) {
          setDelegatedSessions(data.delegated_sessions);
          setDelegatedRecords(data.records);
        }
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    [id, handleSnackbar, getErrorMessage, page]
  );

  const getSessions = useCallback(
    async (search: string) => {
      try {
        const { data } = await getSessionsByClient(
          Number(id),
          search,
          sessionsPage
        );

        if (data.sessions) {
          setSessions(data.sessions);
          setSessionsRecords(data.records);
        }
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      }
    },
    [id, handleSnackbar, getErrorMessage, sessionsPage]
  );

  const refreshDelegatedSessions = (): void => {
    getDelegatedSessions(normalizeStr(debounceDelegatedSessions));
  };

  const refreshSessions = useCallback((): void => {
    getSessions(normalizeStr(debounceSessions));
  }, [getSessions, debounceSessions]);

  const handleCancelDelegatedSession = async (
    sessionId: number
  ): Promise<void> => {
    try {
      const response = await cancelDelegatedSession(sessionId);

      if (response.error) {
        throw new Error(response.error.message);
      }

      handleSnackbar('Solicitação cancelada.', false);
      refreshDelegatedSessions();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleConfirmCancel = async (): Promise<void> => {
    if (cancelTargetId === null) {
      return;
    }
    await handleCancelDelegatedSession(cancelTargetId);
    setCancelTargetId(null);
  };

  const handleStartDelegatedSession = async (
    sessionId: number
  ): Promise<void> => {
    try {
      const response = await startDelegatedSession(sessionId);

      if (response.error) {
        throw new Error(response.error.message);
      }

      const impersonationToken = response.data?.impersonation_token;

      if (impersonationToken) {
        const protoBaseUrl = getCompanySubdomain().includes('-st')
          ? 'https://app-st.protoai.com.br'
          : 'https://protoai.com.br';

        window.open(
          `${protoBaseUrl}/home?impersonation_token=${impersonationToken}`,
          '_blank'
        );
      }

      handleSnackbar('Acesso delegado iniciado!', false);
      refreshDelegatedSessions();
      setTimeout(() => {
        refreshSessions();
      }, 5000);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const renderDelegatedStatus = (row: DelegatedSessions): ReactNode => {
    // console.log('row', row);
    const label = delegatedAccessLabels[row.status] ?? row.status;
    const detail = expiredDetail(row);

    if (!detail) {
      return label;
    }

    return (
      <>
        {label}
        <Tooltip title={detail.tooltip} placement="top">
          <SubtitleCell>{detail.label}</SubtitleCell>
        </Tooltip>
      </>
    );
  };

  const renderSessionStatus = (row: Sessions): ReactNode => {
    if (!row.ended_at) {
      return (
        <div>
          <RoundedGreen />
          Online
        </div>
      );
    }

    return (
      <>
        {getTerminatedByLabel(row.terminated_by)}
        <SubtitleCell>
          <span>{formatDate(row.ended_at, 'absolute')}</span>
        </SubtitleCell>
      </>
    );
  };

  const renderDelegatedAction = (row: DelegatedSessions): ReactNode => {
    const isOwner = loggedSupervisorId === row.supervisor_id;

    if (oversightUserRole !== 'supervisor' || !isOwner) {
      return '-';
    }

    if (row.status === 'pending_consent') {
      return (
        <Button
          variant="outlined"
          isRedButton
          onClick={() => setCancelTargetId(row.id)}
        >
          Cancelar solicitação
        </Button>
      );
    }

    if (row.status === 'approved') {
      return (
        <Button
          variant="contained"
          onClick={() => handleStartDelegatedSession(row.id)}
        >
          Iniciar acesso delegado
        </Button>
      );
    }

    return '-';
  };

  useEffect(() => {
    const loadClientData = async (): Promise<void> => {
      await Promise.all([getClientsById(), getClientsCompany()]);
      setIsLoading(false);
    };

    loadClientData();
  }, []);

  useEffect(() => {
    getDelegatedSessions(normalizeStr(debounceDelegatedSessions));

    // TODO: trocar polling por websocket dedicado assim que o backend
    // corrigir a instabilidade do servidor de desenvolvimento.
    const pollingId = setInterval(() => {
      getDelegatedSessions(normalizeStr(debounceDelegatedSessions));
    }, 10000);

    return () => clearInterval(pollingId);
  }, [debounceDelegatedSessions, page]);

  useEffect(() => {
    getSessions(normalizeStr(debounceSessions));
  }, [debounceSessions, sessionsPage]);

  const applyDelegatedAccessStatusUpdate = useCallback(
    (message: MessageEvent<string>) => {
      const messageData = JSON.parse(message.data);

      if (messageData.type !== WebsocketTypes.DELEGATED_ACCESS_STATUS_UPDATE) {
        return;
      }

      const payload = messageData.payload as DelegatedAccessStatusUpdatePayload;

      setDelegatedSessions((prev) =>
        prev.map((session) =>
          session.id === payload.delegated_session_id
            ? {
                ...session,
                status: payload.status,
                reason: payload.reason,
                consent_expires_at: payload.consent_expires_at,
                approval_expires_at: payload.approval_expires_at,
                session_started_at: payload.session_started_at,
              }
            : session
        )
      );
    },
    []
  );

  useEffect(() => {
    if (!websocketMessage) return;
    applyDelegatedAccessStatusUpdate(websocketMessage);
  }, [websocketMessage]);

  return (
    <>
      <ContainerGroup
        orientation="horizontal"
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
      >
        <Panel id="left" minSize="50%">
          <GridMain
            sx={{
              overflow: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              height: '100%',
              minHeight: 0,

              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <BackButton onClick={() => navigate(-1)}>
              {IconArrowCircleLeftMS}
            </BackButton>
            <StyledTitle>
              {IconAdminPanelSettingsMS} PROTO OVERSIGHT {IconArrowForwardIosMS}{' '}
              Clientes {IconArrowForwardIosMS} {client?.name}
            </StyledTitle>
            <CardContainer>
              <Card
                title="Informações do cliente"
                cardData={infoClient}
                isLoading={isLoading}
              />
              <Card
                title="Informações da empresa"
                cardData={infoCompany}
                isLoading={isLoading}
              />
            </CardContainer>

            <Section>
              <div>
                <TableDataName>Acessos Delegados</TableDataName>
                {isLoading ? (
                  <SkeletonWrapper
                    isLoading={isLoading}
                    variant="text"
                    height={28}
                    width={128}
                  />
                ) : (
                  <AmountOFClients>
                    {delegatedRecords}{' '}
                    {delegatedRecords === 1 ? 'registro' : 'registros'}
                  </AmountOFClients>
                )}
              </div>

              <DelegatedSessionsContainer>
                {oversightUserRole === 'supervisor' && (
                  <Tooltip
                    title={
                      hasBlockingDelegatedAccess ? blockingAccessTooltip : ''
                    }
                    placement="top"
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={handleOpenDialog}
                        disabled={hasBlockingDelegatedAccess}
                      >
                        Solicitar acesso delegado {IconAdminPanelSettingsMS}
                      </Button>
                    </span>
                  </Tooltip>
                )}

                <Search
                  withoutBorder
                  width="375px"
                  placeholder="Buscar por supervisor, status ou motivo..."
                  setSearchField={setSearchDelegatedSessions}
                  disabled={
                    !delegatedSessions.length && !searchDelegatedSessions
                  }
                />
              </DelegatedSessionsContainer>
            </Section>

            <Table
              data={delegatedSessions}
              tableHeadData={delegatedAccessRow}
              getRowId={(row) => row.id}
              emptyTableIcon={<SearchIcon />}
              emptyTableDescription="Nenhum resultado encontrado"
              records={delegatedRecords}
              page={page}
              setPage={setPage}
              renderRow={(row) => (
                <>
                  <StyledTableCellBody>
                    {row.supervisor_name}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    {renderDelegatedStatus(row)}
                  </StyledTableCellBody>
                  <StyledTableCellBody>{row.reason}</StyledTableCellBody>
                  <StyledTableCellBody>
                    {renderDelegatedAction(row)}
                  </StyledTableCellBody>
                </>
              )}
            />

            <Section>
              <div className="values">
                <TableDataName>Sessões do cliente</TableDataName>

                {isLoading ? (
                  <SkeletonWrapper
                    isLoading={isLoading}
                    variant="text"
                    height={28}
                    width={128}
                  />
                ) : (
                  <AmountOFClients>
                    {sessionsRecords}{' '}
                    {sessionsRecords === 1 ? 'sessão' : 'sessões'}
                  </AmountOFClients>
                )}
              </div>

              <Search
                setSearchField={setSearchSessions}
                placeholder="Busca por status ou erro..."
                width="375px"
                withoutBorder
                disabled={!sessions.length && !searchSessions}
              />
            </Section>

            <Table
              data={sessions}
              tableHeadData={sectionClientRow}
              getRowId={(row) => row.id}
              emptyTableIcon={<SearchIcon />}
              emptyTableDescription="Nenhum resultado encontrado"
              records={sessionsRecords}
              page={sessionsPage}
              setPage={setSessionsPage}
              renderRow={(row) => (
                <>
                  <StyledTableCellBody>
                    {formatDate(row.started_at, 'absolute')}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    {renderSessionStatus(row)}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    {row.client_error_count === 0 ? (
                      '-'
                    ) : (
                      <div>
                        <WarningOutlined />
                        {row.client_error_count}
                      </div>
                    )}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    <Button
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenActivity(row);
                      }}
                    >
                      Ver atividade ({row.session_event_count}){IconPageviewMS}
                    </Button>
                  </StyledTableCellBody>
                </>
              )}
            />
          </GridMain>
        </Panel>
        <StyledSeparator className="resize-handle">
          <DragIndicator fontSize="small" />
        </StyledSeparator>
        {showDrawer && (
          <Panel id="right">
            {' '}
            <SectionClient
              sessionId={activitySessionId}
              source="section_client"
              onClose={() => handleClose()}
              onSessionEnded={refreshSessions}
              userRole={oversightUserRole}
              error_count={sectionErrorCount}
              device={device}
              started_at={startedAt}
              ended_at={endedAt}
              delegated_session_id={delegatedSessionId}
            />
          </Panel>
        )}
      </ContainerGroup>
      <DelegatedAccessDialog
        open={openDialogDelegatedAccess}
        handleClose={handleOpenDialog}
        title="Solicitar acesso delegado"
        clientId={client?.id ?? Number(id)}
        clientName={client?.name ?? ''}
        onSuccess={() =>
          getDelegatedSessions(normalizeStr(debounceDelegatedSessions))
        }
      />
      <Dialog
        open={cancelTargetId !== null}
        onClose={() => setCancelTargetId(null)}
      >
        <Box sx={{ padding: '32px', maxWidth: '420px' }}>
          <Typography variant="h6" sx={{ marginBottom: '8px' }}>
            Cancelar solicitação?
          </Typography>
          <Typography sx={{ marginBottom: '24px', color: 'common.grey' }}>
            O cliente não receberá mais notificações sobre este acesso. Você
            poderá enviar uma nova solicitação quando necessário.
          </Typography>
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <Button
              variant="outlined"
              rounded
              style={{ flex: 1 }}
              onClick={() => setCancelTargetId(null)}
            >
              Voltar
            </Button>
            <Button
              variant="outlined"
              rounded
              isRedButton
              style={{ flex: 1 }}
              onClick={handleConfirmCancel}
            >
              Cancelar solicitação
            </Button>
          </Box>
        </Box>
      </Dialog>
      {openSnackbar && <Snackbar />}
    </>
  );
}
